import format from "date-fns/format";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import actions from "@/actions";
import { GetCommentsParams, GetCommentsResult } from "@/components/comment/actions/types";
import {
  COMMENT_INITIAL_PAGER_SEARCH_PARAMS,
  COMMENT_NUM_PER_PAGE,
  COMMENT_SEARCH_STORED_KEY,
} from "@/components/comment/constants/comment";
import { calculateStartPos, getPageFromQueryParam } from "@/lib/pager";
import storageManager from "@/lib/storageManager";
import { CommentSearchParams } from "@/models/comments";

type CommentSearchCondition = Pick<
  GetCommentsParams,
  "category_id" | "comment" | "insert_start_date" | "insert_end_date"
>;

type Props = {
  filterSearchConditionByConfig: (params: CommentSearchParams) => CommentSearchParams;
  setPage: (page: number) => void;
};

export const useSearchInput = ({ filterSearchConditionByConfig, setPage }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // 検索条件
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [comment, setCommentInput] = useState<string>("");
  const [insertStartDate, setInsertStartDate] = useState<Date>();
  const [insertEndDate, setInsertEndDate] = useState<Date>();
  // リアルタイムにコメント列のテキストをハイライトするのでredux stateでも管理する
  const changeComment = useMemo(
    () =>
      _.debounce((value) => {
        dispatch(actions.comment.changeComment(value));
      }, 300),
    []
  );
  const setComment = (value) => {
    setCommentInput(value);
    changeComment(value);
  };
  const [error, setError] = useState<{ comment?: string; date?: string }>({});

  // 共通仕様：検索条件の復元
  useEffect(() => {
    const storedSearchCondition = storageManager.getConstructionItem(COMMENT_SEARCH_STORED_KEY);
    const condition: CommentSearchCondition = storedSearchCondition ? JSON.parse(storedSearchCondition) : null;

    const page = getPageFromQueryParam("p");
    const start = page ? calculateStartPos(page, COMMENT_NUM_PER_PAGE) : 1;

    if (condition) {
      setCategoryIds(condition.category_id);
      setComment(condition.comment ?? "");
      setInsertStartDate(condition.insert_start_date ? new Date(condition.insert_start_date) : null);
      setInsertEndDate(condition.insert_end_date ? new Date(condition.insert_end_date) : null);
      const params = {
        ...COMMENT_INITIAL_PAGER_SEARCH_PARAMS,
        start,
        category_id: condition.category_id,
        insert_start_date: condition.insert_start_date ?? null,
        insert_end_date: condition.insert_end_date ?? null,
        comment: condition.comment,
      };
      dispatch(
        actions.comment.getComments(params, (res: GetCommentsResult) => {
          if (res.list.length === 0 && 0 < res.total_num && res.total_num < params.start) {
            const maxPage = Math.ceil(res.total_num / COMMENT_NUM_PER_PAGE);
            const fixedParams = { ...params, start: calculateStartPos(maxPage, COMMENT_NUM_PER_PAGE) };
            setPage(maxPage);
            dispatch(actions.comment.getComments(fixedParams));
          }
        })
      );
    }
  }, []);

  // 現在の検索条件
  const searchCondition = (): CommentSearchParams => {
    return filterSearchConditionByConfig({
      category_id: categoryIds,
      comment: comment,
      insert_start_date: insertStartDate ? format(insertStartDate, "yyyy-MM-dd") : undefined,
      insert_end_date: insertEndDate ? format(insertEndDate, "yyyy-MM-dd") : undefined,
    });
  };

  // 検索
  const search = () => {
    // バリデーション
    let commentError: string | undefined = undefined;
    let dateError: string | undefined = undefined;
    if (100 < (comment || "").length) {
      commentError = t("comment_too_long");
    }
    if (insertStartDate && insertEndDate && insertEndDate < insertStartDate) {
      dateError = t("invalid_insert_date");
    }
    setError({ comment: commentError, date: dateError });
    if (commentError || dateError) return;

    const condition = searchCondition();
    setComment(condition.comment ?? "");
    setCategoryIds(condition.category_id);
    setInsertStartDate(condition.insert_start_date ? new Date(condition.insert_start_date) : null);
    setInsertEndDate(condition.insert_end_date ? new Date(condition.insert_end_date) : null);

    storageManager.setConstructionItem(COMMENT_SEARCH_STORED_KEY, JSON.stringify(condition));
    dispatch(
      actions.comment.getComments({
        ...condition,
        ...COMMENT_INITIAL_PAGER_SEARCH_PARAMS,
      })
      // reducerで管理しているstateを使うのでcallbackはここで必要ない
    );
  };

  // クリア
  const clear = () => {
    setCategoryIds([]);
    setInsertStartDate(null);
    setInsertEndDate(null);
    setComment("");
    setError({});
    storageManager.removeConstructionItem(COMMENT_SEARCH_STORED_KEY);
    dispatch(
      actions.comment.getComments({
        category_id: [],
        insert_start_date: null,
        insert_end_date: null,
        comment: "",
        start: 1,
        limit: 20,
      })
    );
  };

  return {
    comment,
    setComment,
    categoryIds,
    setCategoryIds,
    insertStartDate,
    setInsertStartDate,
    insertEndDate,
    setInsertEndDate,

    error,

    searchCondition, // 表示設定によってフィルタされた検索条件
    search,
    clear,
  };
};
